import React, { useState, useEffect, useCallback } from "react";
import { useTheme, useUser } from "ogcommon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faPhone,
  faUser,
  faLock,
  faPenFancy,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { baseUrl } from "./config"; // ← NEW: import shared baseUrl

function UserProfile() {
  const { theme } = useTheme();
  const { userID, userRole } = useUser(); // ← include role for role-based redirect

  const [user, setUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedUser, setEditedUser] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [subdomain, setSubdomain] = useState(""); // ← NEW: tenant-aware redirect for admins
  const navigate = useNavigate();

  // ───────────────── subdomain (for admin ticketGrid route) ─────────────────
  useEffect(() => {
    const hostname = window.location.hostname; // e.g., stasney.ogfieldticket.ogpumper.net
    const parts = hostname.split(".");
    if (parts.length > 2) {
      setSubdomain(parts[0] || "");
    } else {
      setSubdomain("");
    }
  }, []);

  // ───────────────── role-only home resolver ─────────────────
  const resolveHomeRoute = useCallback(() => {
    if (userRole === "A") {
      return subdomain ? `/ticketGrid` : "/ticketGrid";
    }
    return "/home";
  }, [userRole, subdomain]);

  /* ───────────────── fetch user details ────────────────────── */
  const fetchUserDetails = useCallback(async () => {
    if (!userID) return;
    try {
      const { data } = await axios.get(`${baseUrl}/api/userdetails.php`, {
        params: { id: encodeURIComponent(userID) },
      });

      // Accept multiple possible response shapes:
      // 1) { success:true, user:{...} }
      // 2) { success:true, users:[...] }
      // 3) { UserID:"...", ... } (raw user object)
      let current = null;

      if (data?.success && Array.isArray(data.users)) {
        current = data.users.find((u) => u.UserID === userID) ?? null;
      } else if (data?.success && data.user && data.user.UserID) {
        current = data.user;
      } else if (data && data.UserID) {
        current = data;
      }

      if (current) {
        // Normalize to avoid uncontrolled inputs
        const normalized = {
          UserID: current.UserID ?? "",
          Sec: current.Sec ?? "",
          Role: current.Role ?? "",
          FullName: current.FullName ?? "",
          Email: current.Email ?? "",
          Phone: current.Phone ?? "",
          GroupID: current.GroupID ?? "",
          Message: current.Message ?? "",
          Address: current.Address ?? "",
          CityStateZip: current.CityStateZip ?? "",
          Misc: current.Misc ?? "",
          AutoAdvance: current.AutoAdvance ?? "",
          google_id: current.google_id ?? "",
          ChartsPref: current.ChartsPref ?? null,
          JobRole: current.JobRole ?? "",
          Disabled: typeof current.Disabled === "number" ? current.Disabled : 0,
          perms: current.perms ?? "",
        };
        setUser(normalized);
        setEditedUser(normalized);
      } else {
        console.error("Failed to fetch user details (no matching user found)");
      }
    } catch (err) {
      console.error("Error fetching user details:", err);
    }
  }, [userID]);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  /* ───────────────── save profile changes ──────────────────── */
  const handleSaveProfile = async () => {
    try {
      const res = await fetch(`${baseUrl}/api/userdetails.php`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editedUser),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setUser(editedUser);
          setEditMode(false);
        }
      } else {
        console.error("Error updating profile:", res.statusText);
      }
    } catch (err) {
      console.error("Error updating profile:", err);
    }
  };

  /* ───────────────── change password ───────────────────────── */
  const handleChangePassword = async () => {
    if (password !== confirmPassword) return;
    try {
      const updatedUser = { UserID: user.UserID, Sec: password };
      const { data } = await axios.patch(
        `${baseUrl}/api/userdetails.php`,
        updatedUser
      );
      if (data.success) {
        setPassword("");
        setConfirmPassword("");
        setShowPasswordChange(false);
      } else {
        console.error("Failed to change password");
      }
    } catch (err) {
      console.error("Error changing password:", err);
    }
  };

  /* ───────────────── helpers ──────────────────────────────── */
  const handleInputChange = ({ target: { name, value } }) =>
    setEditedUser((prev) => ({ ...prev, [name]: value }));

  const handleEditProfile = () => setEditMode(true);
  const handleCancelEdit = () => {
    setEditedUser(user);
    setEditMode(false);
  };

  const getRoleLabel = (role) =>
    ({ P: "Pumper", O: "Operator", A: "Admin", I: "Partner" }[role] ?? "");

  /* ───────────────── render ───────────────────────────────── */
  if (!user) return <div>Loading...</div>;

  return (
    <div
      className={`min-h-screen ${
        theme === "dark" ? "bg-gray-900" : "bg-gray-100"
      } py-8`}
    >
      <div className="container mx-auto px-4">
        <div
          className={`max-w-4xl mx-auto relative ${
            theme === "dark" ? "bg-gray-800" : "bg-white"
          } rounded-lg shadow-lg p-6`}
        >
          {/* ───── back home (role-only) ───── */}
          <button
            onClick={() => navigate(resolveHomeRoute())}
            className={`absolute top-5 right-5 p-2 rounded-full hover:bg-opacity-30 transition-all ${
              theme === "dark" ? "hover:bg-white" : "hover:bg-gray-400"
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className={`w-6 h-6 ${
                theme === "dark" ? "text-white" : "text-gray-800"
              }`}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 9.5V21h7V14h4v7h7V9.5M9 3l3-3 3 3M2 9h20"
              />
            </svg>
          </button>

          {/* ───── profile content ───── */}
          <div
            className={`p-6 ${
              theme === "dark" ? "text-white" : "text-gray-800"
            }`}
          >
            <div className="flex flex-col items-center space-y-6">
              <div className="rounded-full w-24 h-24 bg-gray-300 flex items-center justify-center">
                <FontAwesomeIcon icon={faUser} size="3x" />
              </div>
              <div className="w-full text-center">
                <p className="text-xl font-semibold">{user.FullName}</p>
                <p className="text-gray-400 truncate">
                  {getRoleLabel(user.Role)}
                </p>
              </div>
            </div>

            {/* ───── details grid ───── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              {/* Email */}
              <div className="flex flex-col">
                <div className="flex items-center">
                  <FontAwesomeIcon
                    icon={faEnvelope}
                    className={`h-6 w-6 mr-4 ${
                      theme === "dark" ? "text-white" : "text-black"
                    }`}
                  />
                  <p
                    className={`font-semibold ${
                      theme === "dark" ? "text-white" : "text-black"
                    }`}
                  >
                    Email
                  </p>
                </div>
                {editMode ? (
                  <input
                    type="email"
                    name="Email"
                    value={editedUser.Email}
                    onChange={handleInputChange}
                    className={`text-gray-400 bg-transparent border-b focus:outline-none focus:border-blue-500 w-full ${
                      theme === "dark"
                        ? "border-gray-500 text-white"
                        : "border-gray-300 text-black"
                    }`}
                  />
                ) : (
                  <p
                    className={`truncate mt-2 ${
                      theme === "dark" ? "text-gray-400" : "text-gray-700"
                    }`}
                  >
                    {user.Email}
                  </p>
                )}
              </div>

              {/* Phone */}
              <div className="flex flex-col">
                <div className="flex items-center">
                  <FontAwesomeIcon
                    icon={faPhone}
                    className={`h-6 w-6 mr-4 ${
                      theme === "dark" ? "text-white" : "text-black"
                    }`}
                  />
                  <p
                    className={`font-semibold ${
                      theme === "dark" ? "text-white" : "text-black"
                    }`}
                  >
                    Phone
                  </p>
                </div>
                {editMode ? (
                  <input
                    type="tel"
                    name="Phone"
                    value={editedUser.Phone}
                    onChange={handleInputChange}
                    className={`text-gray-400 bg-transparent border-b focus:outline-none focus:border-blue-500 w-full ${
                      theme === "dark"
                        ? "border-gray-500 text-white"
                        : "border-gray-300 text-black"
                    }`}
                  />
                ) : (
                  <p
                    className={`truncate mt-2 ${
                      theme === "dark" ? "text-gray-400" : "text-gray-700"
                    }`}
                  >
                    {user.Phone}
                  </p>
                )}
              </div>

              {/* UserID */}
              <div className="flex flex-col">
                <div className="flex items-center">
                  <FontAwesomeIcon
                    icon={faLock}
                    className={`h-6 w-6 mr-4 ${
                      theme === "dark" ? "text-white" : "text-black"
                    }`}
                  />
                  <p
                    className={`font-semibold ${
                      theme === "dark" ? "text-white" : "text-black"
                    }`}
                  >
                    UserID
                  </p>
                </div>
                <p
                  className={`truncate mt-2 ${
                    theme === "dark" ? "text-gray-400" : "text-gray-700"
                  }`}
                >
                  {user.UserID}
                </p>
              </div>

              {/* Message (for Admin/Pumper) */}
              {(user.Role === "A" || user.Role === "P") && (
                <div className="flex flex-col">
                  <div className="flex items-center">
                    <FontAwesomeIcon
                      icon={faPenFancy}
                      className={`h-6 w-6 mr-4 ${
                        theme === "dark" ? "text-white" : "text-black"
                      }`}
                    />
                    <p
                      className={`font-semibold ${
                        theme === "dark" ? "text-white" : "text-black"
                      }`}
                    >
                      Message
                    </p>
                  </div>
                  <p
                    className={`truncate mt-2 ${
                      theme === "dark" ? "text-gray-400" : "text-gray-700"
                    }`}
                  >
                    {user.Message}
                  </p>
                </div>
              )}
            </div>

            {/* ───── change password ───── */}
            {showPasswordChange && (
              <div className="mt-8">
                <h2 className="text-xl font-semibold mb-4 text-center">
                  Change Password
                </h2>
                <div className="space-y-4">
                  <input
                    type="password"
                    placeholder="New Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="text-gray-400 bg-transparent border-b border-gray-500 focus:outline-none focus:border-blue-500 w-full"
                  />
                  <input
                    type="password"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="text-gray-400 bg-transparent border-b border-gray-500 focus:outline-none focus:border-blue-500 w-full"
                  />
                  <div className="flex space-x-4">
                    <button
                      className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded w-full"
                      onClick={handleChangePassword}
                    >
                      Save Password
                    </button>
                    <button
                      className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded w-full"
                      onClick={() => setShowPasswordChange(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ───── action buttons ───── */}
            <div className="flex space-x-4 mt-8">
              {editMode ? (
                <>
                  <button
                    className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded w-full"
                    onClick={handleSaveProfile}
                  >
                    Save
                  </button>
                  <button
                    className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded w-full"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded w-full"
                  onClick={() => setEditMode(true)}
                >
                  Edit Profile
                </button>
              )}
              <button
                className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded w-full"
                onClick={() => setShowPasswordChange(true)}
              >
                Change Password
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserProfile;
